<template>
  <div
    class="graffiti-card bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 h-full flex flex-col"
  >
    <!-- Photo miniature -->
    <div class="relative h-48 bg-gray-100 shrink-0">
      <LazyThumbnail
        v-if="graffiti.photo"
        :src="thumbnailUrl"
        :alt="`Photo du graffiti ${graffiti.idBdc}`"
      />
      <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
        <span>Pas de photo</span>
      </div>
    </div>

    <!-- En-tête et contenu -->
    <div class="p-4 flex flex-col flex-grow">
      <div class="flex justify-between items-start mb-2">
        <h3 class="text-lg font-semibold text-gray-900">{{ graffiti.idBdc }}</h3>
        <div class="flex gap-1">
          <router-link
            v-if="graffiti.categorie"
            :to="{
              name: 'categorie-subset',
              params: { categorieName: encodeURIComponent(graffiti.categorie) },
            }"
            class="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-green-100/80 text-green-800 hover:bg-green-200"
            @click.stop
          >
            <Link class="w-3 h-3 mr-1" />
            {{ graffiti.categorie }}
          </router-link>
          <span
            v-if="graffiti.inedit"
            class="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800"
          >
            Inédit
          </span>
        </div>
      </div>

      <!-- Informations principales -->
      <div class="mb-3 space-y-1">
        <div class="flex items-center text-sm text-gray-600">
          <span class="font-medium mr-2">Type:</span>
          <span>{{ graffiti.type }}</span>
        </div>
        <div class="flex items-center text-sm text-gray-600">
          <span class="font-medium mr-2">Dimensions:</span>
          <span>{{ graffiti.dimLon }}×{{ graffiti.dimHau }} cm</span>
        </div>
      </div>

      <!-- Description courte -->
      <p class="text-sm text-gray-500 mb-3 line-clamp-2">
        {{ graffiti.description }}
      </p>

      <!-- Thèmes -->
      <div v-if="graffiti.themes && graffiti.themes.length > 0" class="mt-2">
        <div class="flex flex-wrap gap-2">
          <router-link
            v-for="theme in graffiti.themes"
            :key="theme"
            :to="{ name: 'theme-subset', params: { themeName: encodeURIComponent(theme) } }"
            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-black/80 transition-all duration-200 ease-in-out hover:bg-opacity-90 hover:outline hover:outline-gray-200"
            @click.stop
          >
            <Tag class="w-3 h-3 mr-1" />
            {{ theme }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LazyThumbnail from '@/components/Viewer/LazyThumbnail.vue'
import type { GraffitiWithThemes } from '@/types'
import { getImageUrl } from '@/utils/imageUrl'
import { Link, Tag } from 'lucide-vue-next'
import { computed } from 'vue'

const props = defineProps<{
  graffiti: GraffitiWithThemes
  isListView?: boolean
}>()

// Computed pour l'URL de la miniature avec cache
const thumbnailUrl = computed(() => {
  if (!props.graffiti.photo) return ''

  // Transformer le chemin relatif en URL complète vers le backend
  const relativePath = `/images/graffitis/thumbnails/${props.graffiti.photo}`
  return getImageUrl(relativePath) || relativePath
})
</script>

<style scoped>
/* Styles spécifiques au composant */
</style>
